import { useMemo } from 'react'

import { InjectionTokens } from '@/controller/tokens'

import { ADHP } from '@/domain/models'
import { Coordinates } from '@/domain/protocols/Coordinates'
import { BaseMapType, MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'
import { RouteState, RouteStateProps } from '@/domain/states/RouteState'

import { IAeroInfoRepository } from '@/data/AeroInfoRepository/IAeroInfoRepository'

import { MapHighlightedNotams } from '../components/MapHighlightedNotams'
import { MapView } from '../components/MapView/MapView'
import { MapADHPPresenter } from './MapADHPPresenter'
import { MapAirspacesPresenter } from './MapAirspacesPresenter'
import { MapChartsPresenter } from './MapChartsPresenter'
import { MapFuelPresenter } from './MapFuelPresenter'
import { MapMeasuringSegmentPresenter } from './MapMeasuringSegmentPresenter'
import { MapMeteorologyPresenter } from './MapMeteorologyPresenter'
import { MapNavaidPresenter } from './MapNavaidPresenter'
import { MapObstaclesPresenter } from './MapObstaclesPresenter'
import { MapProcedurePresenter } from './MapProcedurePresenter'
import { MapRoutePresenter } from './MapRoutePresenter'
import { MapSearchPresenter } from './MapSearchPresenter'
import { MapStackPresenter } from './MapStackPresenter'
import { MapTargetPresenter } from './MapTargetPresenter'
import { MapTocAndTodPresenter } from './MapTocAndTodPresenter'
import { MapUserChartsPresenter } from './MapUserChartsPresenter'
import { MapUserGeometriesPresenter } from './MapUserGeometriesPresenter'
import { TrackHeadboardPresenter } from './TrackHeadboardPresenter'

import 'mapbox-gl/dist/mapbox-gl.css'

import type { ILogger } from '@/infra/logger/ILogger'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { AppErrorBoundary } from '@/presentation/modules/Base/components/AppErrorBoundary/AppErrorBoundary'

function MapPresenter() {
  const [mapLayerState] = useGlobalState<MapLayerState, MapLayerStateProps>(InjectionTokens.MapLayerState)
  const [routeState] = useGlobalState<RouteState, RouteStateProps>(InjectionTokens.RouteState)
  const aeroInfoRepository = useInjection<IAeroInfoRepository>(InjectionTokens.AeroInfoRepository)
  const logger = useInjection<ILogger>(InjectionTokens.Logger)

  const baseMapsLinks: Record<BaseMapType, string> = useMemo(
    () => ({
      ROAD: 'mapbox://styles/cristianonexatlas/cm7je64g200ki01qsa9de63jf?optimize=true',
      'ROAD-DARK': 'mapbox://styles/cristianonexatlas/cm7je7opu00st01qv1fzzaoqu?optimize=true',
      TERRAIN: 'mapbox://styles/cristianonexatlas/cm7je6ugd001h01s5dbvk7se6?optimize=true',
      SAT: 'mapbox://styles/cristianonexatlas/clw9nhmev012901oxfbwv3uqq?optimize=true',
      'CL-SAT': 'mapbox://styles/cristianonexatlas/cm1hxmd5b005l01p61gmufh77?optimize=true'
    }),
    []
  )

  const mapStyle = useMemo(() => {
    return baseMapsLinks[mapLayerState.baseMap]
  }, [mapLayerState.baseMap])

  return (
    <MapView
      style={mapStyle}
      defaultPosition={{ center: Coordinates.create({ latitude: 0, longitude: 0 }), zoom: 0.8 }}
    >
      <AppErrorBoundary
        logger={logger}
        boundaryName="MapLayersBoundary"
        title="Falha nas camadas do mapa"
        message="Algumas camadas cartográficas ficaram indisponíveis. Recarregue o app para restaurá-las."
        variant="floatingTopLeft"
      >
        <>
          <MapStackPresenter />
          <MapChartsPresenter />
          <MapAirspacesPresenter />
          <MapMeasuringSegmentPresenter />
        </>
      </AppErrorBoundary>
      <AppErrorBoundary
        logger={logger}
        boundaryName="MapRouteBoundary"
        title="Falha na rota exibida"
        message="Os elementos visuais da rota falharam nesta tela. Recarregue o app para tentar novamente."
        variant="floatingBottomCenter"
      >
        <>
          <MapRoutePresenter />
          <MapTocAndTodPresenter />
          {routeState.activeRoute && (
            <TrackHeadboardPresenter
              adhps={routeState.activeRoute.waypoints
                .filter((waypoint) => waypoint instanceof ADHP)
                .map((wpt) => aeroInfoRepository.getADHPData(wpt.code))
                .filter((wpt) => !!wpt)}
            />
          )}
          <MapHighlightedNotams />
        </>
      </AppErrorBoundary>
      <AppErrorBoundary
        logger={logger}
        boundaryName="MapDynamicOverlaysBoundary"
        title="Falha em sobreposições do mapa"
        message="Uma sobreposição dinâmica do mapa encontrou um erro. Recarregue o app para restaurar essa área."
        variant="floatingTopLeft"
      >
        <>
          <MapMeteorologyPresenter />
          <MapFuelPresenter />
          <MapSearchPresenter />
          <MapObstaclesPresenter />
          <MapUserChartsPresenter />
          <MapUserGeometriesPresenter />
          <MapNavaidPresenter />
          <MapADHPPresenter />
          <MapProcedurePresenter />
          <MapTargetPresenter />
        </>
      </AppErrorBoundary>
    </MapView>
  )
}

export { MapPresenter }
